import { useEffect, useMemo, useRef, useState } from 'react';
import { flightCheckSources, summarizeFlightCheck } from '../services/flightCheck';

const SOURCE_KEYS = ['weather', 'tfr', 'airspace', 'hazards'];

const emptySources = () => SOURCE_KEYS.reduce((acc, key) => {
  acc[key] = { status: 'unknown', reasons: [], pending: false, checkedAt: null };
  return acc;
}, {});

export default function useFlightCheck({ lat, lon, drone, refreshTrigger = 0 }) {
  const requestRef = useRef(0);
  const [sources, setSources] = useState(emptySources);

  const hasLocation = lat != null && lon != null;

  useEffect(() => {
    const requestId = requestRef.current + 1;
    requestRef.current = requestId;

    if (!hasLocation) {
      setSources(emptySources());
      return undefined;
    }

    setSources(SOURCE_KEYS.reduce((acc, key) => {
      acc[key] = { status: 'unknown', reasons: [], pending: true, checkedAt: null };
      return acc;
    }, {}));

    SOURCE_KEYS.forEach(key => {
      flightCheckSources[key]({ lat, lon, drone })
        .then(result => {
          if (requestRef.current !== requestId) return;
          setSources(prev => ({
            ...prev,
            [key]: { ...result, pending: false },
          }));
        })
        .catch(() => {
          if (requestRef.current !== requestId) return;
          setSources(prev => ({
            ...prev,
            [key]: {
              source: key,
              status: 'advisory',
              reasons: [`${key === 'tfr' ? 'TFR' : key.charAt(0).toUpperCase() + key.slice(1)} data unavailable`],
              checkedAt: new Date().toISOString(),
              pending: false,
            },
          }));
        });
    });

    return () => {
      requestRef.current += 1;
    };
  }, [drone, hasLocation, lat, lon, refreshTrigger]);

  return useMemo(() => {
    const completedSources = Object.fromEntries(
      Object.entries(sources).filter(([, result]) => !result.pending && result.status !== 'unknown')
    );
    const provisional = summarizeFlightCheck(completedSources);
    const finalResult = summarizeFlightCheck(sources);
    const pendingChecks = SOURCE_KEYS.filter(key => sources[key]?.pending);
    const completedChecks = SOURCE_KEYS.filter(key => !sources[key]?.pending && sources[key]?.status !== 'unknown');
    const hasAnyResult = completedChecks.length > 0;
    const isComplete = hasLocation && pendingChecks.length === 0;

    return {
      sources,
      cardStatuses: Object.fromEntries(SOURCE_KEYS.map(key => [
        key === 'hazards' ? 'NearbyHazards' : key,
        sources[key]?.status || 'unknown',
      ])),
      status: hasAnyResult ? provisional.status : 'unknown',
      summary: hasAnyResult ? provisional.summary : 'Collecting flight data',
      reasons: hasAnyResult ? provisional.reasons : [],
      finalStatus: finalResult.status,
      finalSummary: finalResult.summary,
      pendingChecks,
      completedChecks,
      hasAnyResult,
      isComplete,
      checkedAt: new Date().toISOString(),
    };
  }, [hasLocation, sources]);
}
